// src/pages/UsuarioDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import UsuariosForm from './UsuariosForm';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Building, HardDrive, Edit, CheckSquare, Package, Shield } from 'lucide-react';
import { User, MapPin, Users, FileText, Ticket, ArrowLeft, Phone, IdCard, Bell, ShieldCheck } from '../components/icons';
import Tooltip from '../components/Tooltip';
import ConfirmationModal from '../components/ConfirmationModal';
import UserPermissionsTab from '../components/UserPermissionsTab';
import MicrosoftO365Tab from '../components/MicrosoftO365Tab';
import EquipoTab from '../components/EquipoTab';
import OnboardingTab from '../components/OnboardingTab';
import api from '../services/api';
import { buildActaHistoricaUrl } from '../config';
import { useToast } from '../contexts/ToastContext';
import { formatUbicacion } from '../utils/locationUtils';
import EmpresaBadge from '../components/EmpresaBadge';

export default function UsuarioDetail() {
  const [editando, setEditando] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [usuario, setUsuario] = useState(null);
  const [activosAsignados, setActivosAsignados] = useState([]);
  const [insumosAsignados, setInsumosAsignados] = useState([]);
  const [ticketsUsuario, setTicketsUsuario] = useState([]);
  const [actasUsuario, setActasUsuario] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'info';
  // la pestaña vive en la URL: al volver a la página se recupera donde estabas
  const setActiveTab = useCallback((id) => {
      setSearchParams((p) => { const n = new URLSearchParams(p); n.set('tab', id); return n }, { replace: true });
  }, [setSearchParams]);
  const [onboarding, setOnboarding] = useState(null);

  // Estado para el modal de confirmación
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    action: null
  });

  const cargarDatosUsuario = useCallback(async () => {
    try {
      setLoading(true);

      // Cargar datos básicos del usuario
      const { data: userData } = await api.get(`/usuarios/${id}`);
      setUsuario(userData);

      // Cargar activos asignados
      try {
        const { data: activosData } = await api.get(`/activos/usuarios/${encodeURIComponent(userData.email)}/activos`);
        setActivosAsignados(activosData);
      } catch (error) {
        // activos no disponibles, continuar
      }

      // Cargar tickets del usuario
      try {
        const { data: ticketsData } = await api.get(`/tickets/usuario/${userData.email}`);
        setTicketsUsuario(ticketsData);
      } catch (error) {
        // tickets no disponibles, continuar
      }

      // Cargar actas del usuario
      try {
        const { data: actasData } = await api.get(`/actas/usuario/${userData.email}`);
        setActasUsuario(actasData);
      } catch (error) {
        setActasUsuario([]);
      }

      // Cargar onboarding persistente
      try {
        const { data: ob } = await api.get(`/onboarding/user/${id}`);
        const parsed = (() => { try { return JSON.parse(ob.data || '{}'); } catch { return {}; } })();
        setOnboarding({ pasos: parsed.pasos || {}, notas: parsed.notas || '' });
      } catch (e) {
        setOnboarding(null);
      }

      // Cargar insumos asignados
      try {
        const { data: insumosData } = await api.get(`/consumibles/usuario/${id}`);
        setInsumosAsignados(insumosData);
      } catch (error) {
        // insumos no disponibles, continuar
      }

    } catch (error) {
      console.error('Error al cargar datos del usuario:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargarDatosUsuario();
  }, [cargarDatosUsuario]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  // Manejador para abrir el modal
  const openStatusModal = (targetStatus) => {
    const isActivating = targetStatus;
    setStatusModal({
      isOpen: true,
      type: isActivating ? 'success' : 'danger',
      title: isActivating ? 'Activar Usuario' : 'Desvincular Usuario',
      message: isActivating
        ? '¿Estás seguro de que deseas ACTIVAR este usuario? Volverá a tener acceso al sistema.'
        : '¿Estás seguro de que deseas DESVINCULAR este usuario? Perderá el acceso al sistema inmediatamente.',
      confirmText: isActivating ? 'Activar' : 'Desvincular',
      action: () => executeStatusChange(targetStatus)
    });
  };

  // Manejador para abrir el modal de recordatorio
  const openReminderModal = () => {
    setStatusModal({
      isOpen: true,
      type: 'info',
      title: 'Enviar Recordatorio',
      message: '¿Estás seguro de enviar un correo de recordatorio al usuario sobre sus pendientes?',
      confirmText: 'Enviar Correo',
      action: executeReminder
    });
  };

  // Función que ejecuta el cambio de estado
  const executeStatusChange = async (targetActive) => {
    try {
      setLoading(true);
      await api.put(`/usuarios/${id}`, { activo: targetActive });
      await api.put(`/auth/${id}`, { isActive: targetActive }).catch(() => null);

      showToast(
        'Éxito',
        targetActive
          ? 'Usuario activado exitosamente'
          : 'Usuario inactivado y desvinculado. Sus activos asignados fueron devueltos y sus accesos revocados.',
        'success'
      );

      // Recargar todos los datos del usuario (nómina, activos, actas, etc.)
      await cargarDatosUsuario();
    } catch (err) {
      console.error('Error al cambiar estado:', err);
      showToast('Error', 'Error al cambiar el estado del usuario', 'error');
    } finally {
      setLoading(false);
      setStatusModal(prev => ({ ...prev, isOpen: false }));
    }
  };

  // Función que ejecuta el envío de recordatorio
  const executeReminder = async () => {
    try {
      setLoading(true);
      await api.post('/notifications/reminder', { userId: Number(id) });
      showToast('Éxito', 'Recordatorio enviado exitosamente', 'success');
    } catch (error) {
      console.error('Error enviando recordatorio:', error);
      showToast('Error', 'Error al enviar recordatorio: ' + (error.response?.data?.message || 'Error desconocido'), 'error');
    } finally {
      setLoading(false);
      setStatusModal(prev => ({ ...prev, isOpen: false }));
    }
  };

  if (!usuario) {
    return (
      <div className="p-6">
        <div className="text-center">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Usuario no encontrado</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">El usuario que buscas no existe o ha sido eliminado.</p>
        </div>
      </div>
    );
  }

  const getInitials = (nombre, apellido) => {
    if (nombre && apellido) {
      return `${nombre[0]}${apellido[0]}`.toUpperCase();
    }
    return 'U';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'activo':
      case 'asignado':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'inactivo':
      case 'cerrado':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Función para obtener tickets activos (pendiente, asignado, en proceso)
  const getTicketsActivos = (tickets) => {
    if (!Array.isArray(tickets)) return [];
    return tickets.filter(ticket =>
      ticket.estado === 'Pendiente' ||
      ticket.estado === 'Asignado' ||
      ticket.estado === 'En Proceso' ||
      ticket.estado === 'Esperando Usuario'
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center relative">
              <span className="text-xl font-bold text-white">
                {getInitials(usuario.nombre, usuario.apellido)}
              </span>
              {/* Luz de estado en el avatar */}
              <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white dark:border-gray-900 ${usuario.activo ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]'}`} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span>{usuario.nombre} {usuario.apellido}</span>
                <EmpresaBadge empresa={usuario.empresa} />
                {(() => {
                  const stepKeys = ['rcp', 'cuentas', 'lic', 'equipo', 'valid', 'acta', 'cierre']
                  // Si onboarding es null, asumimos que no es requerido (done=total=7 para no mostrar alerta)
                  // Pero para calculos, si es null, done=0 y total=7 daria alerta.
                  // Mejor logica: si onboarding es null, no hay steps pendientes.
                  const onboardingRequired = !!onboarding;
                  const done = onboardingRequired ? stepKeys.filter(k => onboarding.pasos && onboarding.pasos[k]).length : 0
                  const total = stepKeys.length;
                  const onboardingPending = onboardingRequired && done < total;
                  const actasArr = Array.isArray(actasUsuario) ? actasUsuario : []
                  const actasPend = actasArr.filter(a => a.estado === 'Pendiente').length
                  const actasRech = actasArr.filter(a => a.estado === 'Rechazada').length
                  const actasTotal = actasArr.length
                  const showWarnActa = actasTotal === 0 || actasPend > 0 || actasRech > 0
                  const actaLabel = actasTotal === 0 ? 'Sin actas' : (actasRech > 0 ? `Rechazadas: ${actasRech}` : actasPend > 0 ? `Pendientes: ${actasPend}` : 'OK')
                  const actaColor = actasTotal === 0 ? 'text-gray-300' : (actasRech > 0 ? 'text-red-300' : actasPend > 0 ? 'text-yellow-300' : 'text-green-300')

                  // Obtener tickets activos
                  const ticketsActivos = getTicketsActivos(ticketsUsuario)
                  const showWarnTickets = ticketsActivos.length > 0

                  // Mostrar tooltip si hay problemas con onboarding, actas o tickets
                  const hasWarnings = onboardingPending || showWarnActa || showWarnTickets;
                  // Removed early return to always show bell



                  const actaHint = actasTotal === 0
                    ? 'Genera el acta de entrega.'
                    : (actasRech > 0 ? 'Acta rechazada: vuelve a emitir.' : (actasPend > 0 ? 'Acta pendiente de firma.' : ''))

                  const badgeBg = actasRech > 0 ? 'bg-red-500' : (actasPend > 0 || actasTotal === 0 || showWarnTickets) ? 'bg-yellow-500' : 'bg-green-500'

                  return (
                    <div className="flex items-center gap-2">
                      {/* Badge de Alerta (Solo si hay warnings) */}
                      {hasWarnings && (
                        <Tooltip content={(
                          <div className="flex flex-col gap-0.5">
                            {onboardingPending && (
                              <>
                                <span className=""><strong>Onboarding:</strong> {done}/{total}</span>
                                <span className="text-yellow-300">Completa el checklist para finalizar</span>
                              </>
                            )}
                            {showWarnActa && (
                              <>
                                <span className={'text-white'}><strong>Actas:</strong> {actaLabel}</span>
                                {actaHint && (
                                  <span className={'text-yellow-300'}>{actaHint}</span>
                                )}
                              </>
                            )}
                            {showWarnTickets && (
                              <>
                                <span className={'text-white'}><strong>Tickets:</strong> {ticketsActivos.length} activo{ticketsActivos.length !== 1 ? 's' : ''}</span>
                                <span className={'text-yellow-300'}>Tickets pendientes de resolución</span>
                              </>
                            )}
                          </div>
                        )}>
                          <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${badgeBg} text-white text-xs font-bold`}>!</span>
                        </Tooltip>
                      )}

                      {/* Botón de Recordatorio Manual (Siempre visible, disabled si no hay pendientes enviables) */}
                      <Tooltip content={showWarnActa || showWarnTickets ? "Enviar recordatorio de pendientes por correo" : "No hay pendientes para notificar"}>
                        <button
                          disabled={!showWarnActa && !showWarnTickets}
                          onClick={(e) => {
                            e.stopPropagation();
                            openReminderModal();
                          }}
                          className={`p-1 rounded-full transition-colors ${showWarnActa || showWarnTickets
                            ? 'bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                            }`}
                        >
                          <Bell className="w-5 h-5" />
                        </button>
                      </Tooltip>
                    </div>
                  )
                })()}
              </h1>
              <div className="flex items-center gap-3">
                <p className="text-gray-500 dark:text-gray-400">{usuario.email}</p>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${usuario.activo ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                  {usuario.activo ? 'VINCULADO' : 'DESVINCULADO'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row w-full md:w-auto items-stretch sm:items-center justify-start md:justify-end gap-3">
          {/* Toggle de Estado */}
          <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => !usuario.activo && openStatusModal(true)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-md transition-all ${usuario.activo
                ? 'bg-white dark:bg-gray-700 text-green-600 dark:text-green-400 shadow-sm cursor-default'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              disabled={usuario.activo}
            >
              <div className={`w-2 h-2 rounded-full ${usuario.activo ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-xs font-semibold">Activo</span>
            </button>
            <button
              onClick={() => usuario.activo && openStatusModal(false)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-md transition-all ${!usuario.activo
                ? 'bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 shadow-sm cursor-default'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
              disabled={!usuario.activo}
            >
              <div className={`w-2 h-2 rounded-full ${!usuario.activo ? 'bg-red-500' : 'bg-gray-400'}`} />
              <span className="text-xs font-semibold">Inactivo</span>
            </button>
          </div>

          <button
            onClick={() => setEditando(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-md transform active:scale-95"
          >
            <Edit className="h-4 w-4" />
            <span>Editar perfil</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto scrollbar-hide">
          <nav className="-mb-px flex space-x-8 min-w-max px-4">
            {[
              { id: 'info', label: 'Información', icon: User },
              { id: 'equipo', label: 'Equipo', icon: Users },
              { id: 'activos', label: 'Activos', icon: HardDrive, count: activosAsignados.length + insumosAsignados.length },
              { id: 'tickets', label: 'Tickets', icon: Ticket, count: ticketsUsuario.length },
              { id: 'actas', label: 'Actas', icon: FileText, count: actasUsuario.length },
              { id: 'o365', label: 'Microsoft 365 / AD', icon: ShieldCheck },
              { id: 'aplicaciones', label: 'Aplicaciones', icon: Shield },
              { id: 'onboarding', label: 'Onboarding', icon: CheckSquare }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                <tab.icon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                {tab.count !== undefined && (
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'info' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Información Personal */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Información Personal
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <IdCard className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">RUT</p>
                    <p className="font-medium text-gray-900 dark:text-white">{usuario.rut}</p>
                  </div>
                </div>
                {usuario.username && (
                  <div className="flex items-center space-x-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Usuario</p>
                      <p className="font-medium text-gray-900 dark:text-white">{usuario.username}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Correo Electrónico</p>
                    <p className="font-medium text-gray-900 dark:text-white">{usuario.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Cargo</p>
                    <p className="font-medium text-gray-900 dark:text-white">{usuario.cargo || 'No especificado'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Teléfono</p>
                    <p className="font-medium text-gray-900 dark:text-white">{usuario.telefonoCorporativo || 'No especificado'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Información Laboral */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Building className="h-5 w-5 mr-2 text-blue-600" />
                Información Laboral
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Building className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Empresa</p>
                    <p className="font-medium text-gray-900 dark:text-white">{usuario.empresa}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Departamento</p>
                    <p className="font-medium text-gray-900 dark:text-white">{usuario.departamento}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Ubicación</p>
                    <p className="font-medium text-gray-900 dark:text-white">{formatUbicacion(usuario.ubicacion) || 'No especificada'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'equipo' && (
          <EquipoTab email={usuario?.email} />
        )}

        {activeTab === 'onboarding' && (
          <OnboardingTab usuarioId={id} onboarding={onboarding} setOnboarding={setOnboarding} />
        )}

        {activeTab === 'activos' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <HardDrive className="h-5 w-5 mr-2 text-blue-600" />
                Activos Asignados ({activosAsignados.length})
              </h3>
            </div>
            <div className="p-6">
              {activosAsignados.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activosAsignados.map((activo) => (
                    <div
                      key={activo.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/activos/${activo.codigo}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">{activo.codigo}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activo.estado)}`}>
                          {activo.estado}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{activo.nombreEquipo}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">{activo.categoria}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <HardDrive className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Sin activos asignados</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Este usuario no tiene activos físicos asignados actualmente.</p>
                </div>
              )}
            </div>

            {/* Sección de Insumos */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                <Package className="h-5 w-5 mr-2 text-blue-600" />
                Insumos Asignados ({insumosAsignados.length})
              </h3>
              {insumosAsignados.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {insumosAsignados.map((insumo) => (
                    <div
                      key={insumo.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/consumibles`)} // O llevar al detalle del insumo si existe
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white truncate">{insumo.consumible.nombre}</h4>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                          {insumo.cantidad} unid.
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-500">{insumo.consumible.categoria}</p>
                      <p className="text-[10px] text-gray-400 mt-2">{formatDate(insumo.fecha)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Sin insumos asignados</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Este usuario no tiene insumos registrados.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <Ticket className="h-5 w-5 mr-2 text-blue-600" />
                Tickets del Usuario ({ticketsUsuario.length})
              </h3>
            </div>
            <div className="p-6">
              {ticketsUsuario.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ticketsUsuario.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 flex items-center">
                          <h4 className="font-medium text-gray-900 dark:text-white mr-2">#{ticket.id}</h4>
                          {ticket.descripcion && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate flex-1">
                              {ticket.descripcion}
                            </p>
                          )}
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.estado)} ml-2 flex-shrink-0`}>
                          {ticket.estado}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-500 mt-1">
                        <span className="truncate max-w-[60%]">{ticket.categoria}</span>
                        <span>{formatDate(ticket.fechaCreacion)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Ticket className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Sin tickets</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Este usuario no tiene tickets registrados.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'actas' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                Actas del Usuario ({actasUsuario.length})
              </h3>
            </div>
            <div className="p-6">
              {actasUsuario.length > 0 ? (
                <div className="space-y-4">
                  {actasUsuario.map((acta) => (
                    <div
                      key={acta.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer relative"
                      onClick={() => {
                        if (acta.esHistorica && acta.rutaArchivo) {
                          window.open(buildActaHistoricaUrl(acta.rutaArchivo), '_blank');
                        } else {
                          navigate(`/actas/previsualizar-firmado/${acta.id}`);
                        }
                      }}
                    >
                      {/* Badge pequeño para actas históricas */}
                      {acta.esHistorica && (
                        <div className="absolute top-2 right-2 flex items-center gap-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                          <FileText className="w-3 h-3" />
                          Histórica
                        </div>
                      )}

                      <div className="flex items-center justify-between mb-2 pr-20">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {acta.esHistorica
                            ? (acta.activo ? `Acta Histórica - ${acta.activo.codigo}` : 'Acta de Entrega Histórica')
                            : `Acta de Asignación - ${acta.asignacion?.activo?.codigo || 'N/A'}`
                          }
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(acta.estado)}`}>
                          {acta.estado}
                        </span>
                      </div>

                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {acta.esHistorica ? (
                          <>
                            <strong>Vínculo:</strong> {acta.activo ? `${acta.activo.categoria} - ${acta.activo.nombreEquipo}` : 'Subida manual (Sin activo enlazado)'}
                            {acta.nombreArchivo && <><br /><strong>Archivo:</strong> {acta.nombreArchivo}</>}
                          </>
                        ) : (
                          <>
                            <strong>Activo:</strong> {acta.asignacion?.activo?.categoria} - {acta.asignacion?.activo?.nombreEquipo}
                          </>
                        )}
                      </p>

                      {acta.observaciones && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 whitespace-pre-line">
                          <strong>Observaciones:</strong> {acta.observaciones}
                        </p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-500">
                        <div>
                          <span className="font-medium">Método de firma:</span> {acta.metodoFirma || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Creada:</span> {formatDate(acta.fechaCreacion)}
                        </div>
                        {acta.fechaFirma && (
                          <div>
                            <span className="font-medium">Firmada:</span> {formatDate(acta.fechaFirma)}
                          </div>
                        )}
                        {acta.fechaAprobacion && (
                          <div>
                            <span className="font-medium">Aprobada:</span> {formatDate(acta.fechaAprobacion)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Sin actas</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Este usuario no tiene actas registradas.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'o365' && (
          <MicrosoftO365Tab usuario={usuario} />
        )}

        {activeTab === 'aplicaciones' && (
          <UserPermissionsTab
            userId={id}
            userEmail={usuario.email}
            isActive={usuario.activo}
            userNombre={usuario.nombre}
            userApellido={usuario.apellido}
            userUsername={usuario.username}
            userEmpresa={usuario.empresa}
          />
        )}
      </div>

      {/* Modal de Confirmación */}
      <ConfirmationModal
        isOpen={statusModal.isOpen}
        onClose={() => setStatusModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={statusModal.action}
        title={statusModal.title}
        message={statusModal.message}
        type={statusModal.type}
        confirmText={statusModal.confirmText}
      />
      {editando && (
        <UsuariosForm modal edit idProp={id} onClose={() => setEditando(false)} onSaved={() => cargarDatosUsuario()} />
      )}
    </div>
  );
}
